/**
 * Splits chat message text into plain-text and link segments so that URLs sent
 * in a conversation (e.g. a workplace sharing a link with a worker) can render
 * as clickable links instead of inert text.
 *
 * Detection is intentionally conservative: only `http(s)://` URLs and bare
 * `www.`-prefixed hosts are matched. Other schemes — notably `javascript:` and
 * `data:` — never match, so a rendered link can never carry an executable href.
 */

export interface MessageTextSegment {
  type: "text";
  content: string;
}

export interface MessageLinkSegment {
  type: "link";
  /** The text to display — the matched URL exactly as it appeared in the message. */
  content: string;
  /** The absolute, http(s) href to navigate to. */
  href: string;
}

export type MessageSegment = MessageTextSegment | MessageLinkSegment;

// Matches an http(s) URL or a bare `www.` host, consuming up to the next
// whitespace. Trailing sentence punctuation is trimmed separately (see
// `trimTrailingPunctuation`) so it is not swallowed into the link.
const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s]+/gi;

// Punctuation that commonly follows a URL in prose and should not be part of it.
const TRAILING_PUNCTUATION = new Set([".", ",", ";", ":", "!", "?", '"', "'", "]", "}", "»", "…"]);

/**
 * Trims trailing prose punctuation from a matched URL, returning the cleaned
 * URL and the trimmed suffix (so the suffix can be re-emitted as plain text).
 *
 * A closing parenthesis is kept only when it balances an opening parenthesis
 * inside the URL (e.g. Wikipedia article links); an unbalanced `)` is treated
 * as trailing punctuation.
 */
function trimTrailingPunctuation(rawUrl: string): { url: string; trailing: string } {
  // Count parentheses once. Trimming only ever removes trailing ")" and
  // punctuation — never "(" — so the number of "(" in the kept prefix is
  // invariant, and we can decrement a running ")" balance instead of
  // re-scanning the prefix on every iteration (which would be O(n^2)).
  let openCount = 0;
  let closeCount = 0;
  for (const char of rawUrl) {
    if (char === "(") {
      openCount += 1;
    } else if (char === ")") {
      closeCount += 1;
    }
  }

  let end = rawUrl.length;
  while (end > 0) {
    const char = rawUrl[end - 1];

    if (char === ")") {
      // Keep a ")" that balances an earlier "("; strip an excess (unbalanced) one.
      if (closeCount <= openCount) {
        break;
      }
      closeCount -= 1;
      end -= 1;
    } else if (TRAILING_PUNCTUATION.has(char)) {
      end -= 1;
    } else {
      break;
    }
  }

  return { url: rawUrl.slice(0, end), trailing: rawUrl.slice(end) };
}

function toHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

/**
 * Parses `text` into an ordered list of text and link segments. When `text`
 * contains no links, returns a single text segment with the original string.
 */
export function detectMessageLinks(text: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const matchIndex = match.index ?? 0;
    const rawMatch = match[0];
    const { url, trailing } = trimTrailingPunctuation(rawMatch);

    // A match that trims down to nothing (e.g. a lone "www.") is not a link.
    if (url.length === 0) {
      continue;
    }

    if (matchIndex > lastIndex) {
      segments.push({ type: "text", content: text.slice(lastIndex, matchIndex) });
    }

    segments.push({ type: "link", content: url, href: toHref(url) });

    if (trailing.length > 0) {
      segments.push({ type: "text", content: trailing });
    }

    lastIndex = matchIndex + rawMatch.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", content: text.slice(lastIndex) });
  }

  return segments;
}
